import { useState, useEffect } from 'react';
import {
  X,
  Eye,
  Clock,
  Globe,
  Layout,
  Volume2,
  RefreshCw,
  LogIn,
  LogOut,
  TrendingUp,
  TrendingDown,
  Calendar,
  AlertTriangle,
  UserPlus,
  Download,
  Lock
} from 'lucide-react';
import { auth } from '../firebase';
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  User
} from 'firebase/auth';
import { clearAllStats, getStatsForDateRange, getDateRangeForPreset, getPreviousPeriodRange, DateRangeStatsData, DateRangePreset, setAdminSession } from '../services/statsService';

interface AdminDashboardProps {
  onClose: () => void;
}

const AdminDashboard = ({ onClose }: AdminDashboardProps) => {
  const [user, setUser] = useState<User | null>(null);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [rangeStats, setRangeStats] = useState<DateRangeStatsData | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [showAllSections, setShowAllSections] = useState(false);
  const [dateRangePreset, setDateRangePreset] = useState<DateRangePreset>('last7days');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [activeStartDate, setActiveStartDate] = useState('');
  const [activeEndDate, setActiveEndDate] = useState('');
  const [newAdminEmail, setNewAdminEmail] = useState('');
  const [newAdminPassword, setNewAdminPassword] = useState('');
  const [adminCreateMsg, setAdminCreateMsg] = useState('');
  const [adminCreateError, setAdminCreateError] = useState('');
  const [creatingAdmin, setCreatingAdmin] = useState(false);
  const [previousStats, setPreviousStats] = useState<DateRangeStatsData | null>(null);
  const [clearPassword, setClearPassword] = useState('');
  const [clearPasswordError, setClearPasswordError] = useState('');

  // Listen for auth state changes
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
      setLoading(false);
      setAdminSession(!!currentUser);
      if (currentUser) {
        loadStats();
      }
    });
    return () => {
      unsubscribe();
      setAdminSession(false);
    };
  }, []);

  const loadStats = async (preset?: DateRangePreset) => {
    setRefreshing(true);
    try {
      const currentPreset = preset || dateRangePreset;
      let startDate: string;
      let endDate: string;

      if (currentPreset === 'custom' && customStartDate && customEndDate) {
        startDate = customStartDate;
        endDate = customEndDate;
      } else {
        const range = getDateRangeForPreset(currentPreset);
        startDate = range.startDate;
        endDate = range.endDate;
      }

      setActiveStartDate(startDate);
      setActiveEndDate(endDate);

      const [data, prevRange] = await Promise.all([
        getStatsForDateRange(startDate, endDate),
        Promise.resolve(getPreviousPeriodRange(startDate, endDate))
      ]);
      setRangeStats(data);

      // Fetch previous period for comparison
      const prevData = await getStatsForDateRange(prevRange.startDate, prevRange.endDate);
      setPreviousStats(prevData);
    } catch (err) {
      console.error('Error loading stats:', err);
    }
    setRefreshing(false);
  };

  const handlePresetChange = (preset: DateRangePreset) => {
    setDateRangePreset(preset);
    if (preset !== 'custom') {
      loadStats(preset);
    }
  };

  const handleCustomDateApply = () => {
    if (customStartDate && customEndDate) {
      if (customStartDate > customEndDate) {
        const temp = customStartDate;
        setCustomStartDate(customEndDate);
        setCustomEndDate(temp);
      }
      setDateRangePreset('custom');
      loadStats('custom');
    }
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    try {
      await signInWithEmailAndPassword(auth, email, password);
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Login failed';
      setError(errorMessage);
    }
  };

  const handleLogout = async () => {
    try {
      setAdminSession(false);
      await signOut(auth);
      setRangeStats(null);
    } catch (err) {
      console.error('Logout error:', err);
    }
  };

  const handleCreateAdmin = async (e: React.FormEvent) => {
    e.preventDefault();
    setAdminCreateMsg('');
    setAdminCreateError('');

    if (!newAdminEmail || !newAdminPassword) {
      setAdminCreateError('Please enter both email and password.');
      return;
    }
    if (newAdminPassword.length < 6) {
      setAdminCreateError('Password must be at least 6 characters.');
      return;
    }

    setCreatingAdmin(true);
    try {
      await createUserWithEmailAndPassword(auth, newAdminEmail, newAdminPassword);

      // Firebase auto-signs in the new user, so sign out and re-sign in as current admin
      // Note: the current admin will need to re-login if we can't restore session
      setAdminCreateMsg(`Admin account created for ${newAdminEmail}`);
      setNewAdminEmail('');
      setNewAdminPassword('');

      // Sign out the newly created user (Firebase auto-signs them in)
      await signOut(auth);

      // Alert the admin they need to log back in
      setAdminCreateMsg(`Admin account created for ${newAdminEmail}. Please log back in with your credentials.`);
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to create admin';
      setAdminCreateError(errorMessage);
    }
    setCreatingAdmin(false);
  };

  const formatDuration = (seconds: number) => {
    if (seconds < 60) return `${seconds}s`;
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}m ${secs}s`;
  };

  const getLanguageName = (code: string) => {
    const names: Record<string, string> = {
      en: 'English',
      es: 'Spanish',
      so: 'Somali',
      quc: "K'iche'"
    };
    return names[code] || code;
  };

  const getSectionName = (id: string) => {
    const names: Record<string, string> = {
      home: 'Home',
      appointments: 'Appointments',
      health: 'Health',
      'mental-health': 'Mental Health',
      medications: 'Medications',
      conditions: 'Conditions',
      warning: 'Warning Signs',
      labor: 'Labor & Delivery',
      'birth-control': 'Birth Control',
      breastfeeding: 'Feeding',
      'baby-care': 'Baby Care',
      resources: 'Resources',
      nutrition: 'Nutrition'
    };
    return names[id] || id;
  };

  const getPercentChange = (current: number, previous: number): { value: number; label: string } | null => {
    if (previous === 0 && current === 0) return null;
    if (previous === 0) return { value: 100, label: '+100%' };
    const change = Math.round(((current - previous) / previous) * 100);
    return { value: change, label: `${change >= 0 ? '+' : ''}${change}%` };
  };

  const getTtsEntryName = (key: string) => {
    // Keys are stored as "section_language" e.g. "home-welcome_en"
    const lastUnderscore = key.lastIndexOf('_');
    if (lastUnderscore === -1) return key;
    const section = key.substring(0, lastUnderscore);
    const lang = key.substring(lastUnderscore + 1);
    const sectionLabel = getSectionName(section) || section.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
    return `${sectionLabel} (${getLanguageName(lang)})`;
  };

  const exportToCsv = () => {
    if (!rangeStats) return;
    const lines: string[] = [];
    lines.push(`Kin Analytics Export - ${activeStartDate} to ${activeEndDate}`);
    lines.push('');

    // Summary
    lines.push('SUMMARY');
    lines.push(`Total Visits,${rangeStats.totalVisits}`);
    const avgPerDay = rangeStats.dailyVisits.length > 0 ? Math.round(rangeStats.totalVisits / rangeStats.dailyVisits.length) : 0;
    lines.push(`Avg Visits/Day,${avgPerDay}`);
    lines.push(`Avg Session Duration (seconds),${rangeStats.avgSessionDuration}`);
    lines.push(`Total Sessions,${rangeStats.totalSessions}`);
    lines.push(`TTS Plays,${rangeStats.ttsUsage}`);
    lines.push('');

    // Daily visits
    lines.push('DAILY VISITS');
    lines.push('Date,Visits');
    rangeStats.dailyVisits.forEach(d => lines.push(`${d.date},${d.visits}`));
    lines.push('');

    // Languages
    lines.push('LANGUAGE USAGE');
    lines.push('Language,Count');
    Object.entries(rangeStats.languages)
      .filter(([k]) => ['en', 'es', 'so', 'quc'].includes(k))
      .sort(([, a], [, b]) => (b as number) - (a as number))
      .forEach(([lang, count]) => lines.push(`${getLanguageName(lang)},${count}`));
    lines.push('');

    // Sections
    lines.push('SECTION VIEWS');
    lines.push('Section,Views');
    Object.entries(rangeStats.sections)
      .sort(([, a], [, b]) => (b as number) - (a as number))
      .forEach(([section, count]) => lines.push(`${getSectionName(section)},${count}`));
    lines.push('');

    // TTS breakdown
    lines.push('TTS BREAKDOWN');
    lines.push('Section (Language),Plays');
    Object.entries(rangeStats.ttsBreakdown)
      .sort(([, a], [, b]) => (b as number) - (a as number))
      .forEach(([key, count]) => lines.push(`${getTtsEntryName(key)},${count}`));
    lines.push('');

    // All-time
    lines.push('ALL-TIME TOTALS');
    lines.push(`Total Visits,${rangeStats.allTimeTotals.totalVisits}`);
    lines.push(`Unique Visitors,${rangeStats.allTimeTotals.uniqueVisitors}`);
    lines.push(`Return Visitors,${rangeStats.allTimeTotals.returnVisitors}`);

    const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `kin-analytics-${activeStartDate}-to-${activeEndDate}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleClearWithPassword = async () => {
    if (!clearPassword) {
      setClearPasswordError('Please enter your password.');
      return;
    }
    setClearPasswordError('');
    setClearing(true);
    try {
      // Re-authenticate to verify password
      await signInWithEmailAndPassword(auth, user!.email!, clearPassword);
      await clearAllStats();
      setRangeStats(null);
      setPreviousStats(null);
      await loadStats();
      setShowClearConfirm(false);
      setClearPassword('');
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed';
      if (msg.includes('wrong-password') || msg.includes('invalid-credential')) {
        setClearPasswordError('Incorrect password.');
      } else {
        setClearPasswordError(msg);
      }
    }
    setClearing(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  // Login form if not authenticated
  if (!user) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-100 to-gray-200 p-4">
        <div className="max-w-md mx-auto mt-20">
          <div className="bg-white rounded-2xl shadow-xl p-8">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-gray-800">Admin Login</h2>
              <button
                onClick={onClose}
                className="p-2 hover:bg-gray-100 rounded-full"
              >
                <X className="w-6 h-6 text-gray-500" />
              </button>
            </div>

            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="admin@example.com"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Password
                </label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="••••••••"
                  required
                />
              </div>

              {error && (
                <div className="bg-red-50 text-red-600 px-4 py-3 rounded-xl text-sm">
                  {error}
                </div>
              )}

              <button
                type="submit"
                className="w-full bg-gradient-to-r from-blue-500 to-blue-600 text-white py-3 rounded-xl font-semibold hover:from-blue-600 hover:to-blue-700 transition-all flex items-center justify-center gap-2"
              >
                <LogIn className="w-5 h-5" />
                Sign In
              </button>
            </form>

            <p className="mt-4 text-sm text-gray-500 text-center">
              Only authorized administrators can access this dashboard.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Admin Dashboard
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-100 to-gray-200 p-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-800">Admin Dashboard</h1>
              <p className="text-sm text-gray-500">Prenatal Care App Analytics</p>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={exportToCsv}
                disabled={!rangeStats}
                className="flex items-center gap-1.5 px-3 py-2 bg-green-50 hover:bg-green-100 text-green-700 rounded-xl transition-colors text-sm font-medium disabled:opacity-50"
                title="Export to CSV"
              >
                <Download className="w-4 h-4" />
                Export
              </button>
              <button
                onClick={() => loadStats()}
                disabled={refreshing}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                title="Refresh Stats"
              >
                <RefreshCw className={`w-5 h-5 text-gray-600 ${refreshing ? 'animate-spin' : ''}`} />
              </button>
              <button
                onClick={handleLogout}
                className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-xl transition-colors"
              >
                <LogOut className="w-4 h-4" />
                Logout
              </button>
              <button
                onClick={onClose}
                className="p-2 hover:bg-gray-100 rounded-full"
              >
                <X className="w-6 h-6 text-gray-500" />
              </button>
            </div>
          </div>
        </div>

        {/* Date Range Filter */}
        <div className="bg-white rounded-2xl shadow-lg p-4 mb-6">
          <div className="flex flex-col md:flex-row md:items-center gap-3">
            <div className="flex flex-wrap gap-2">
              {([
                { key: 'today' as DateRangePreset, label: 'Today' },
                { key: 'last7days' as DateRangePreset, label: 'Last 7 Days' },
                { key: 'last30days' as DateRangePreset, label: 'Last 30 Days' },
                { key: 'thisMonth' as DateRangePreset, label: 'This Month' },
                { key: 'custom' as DateRangePreset, label: 'Custom' },
              ]).map(({ key, label }) => (
                <button
                  key={key}
                  onClick={() => handlePresetChange(key)}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                    dateRangePreset === key
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
            {dateRangePreset === 'custom' && (
              <div className="flex items-center gap-2">
                <input
                  type="date"
                  value={customStartDate}
                  onChange={(e) => setCustomStartDate(e.target.value)}
                  className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm"
                />
                <span className="text-gray-400 text-sm">to</span>
                <input
                  type="date"
                  value={customEndDate}
                  onChange={(e) => setCustomEndDate(e.target.value)}
                  className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm"
                />
                <button
                  onClick={handleCustomDateApply}
                  disabled={!customStartDate || !customEndDate}
                  className="px-3 py-1.5 bg-blue-600 text-white rounded-lg text-sm font-medium disabled:opacity-50 hover:bg-blue-700 transition-colors"
                >
                  Apply
                </button>
              </div>
            )}
            {activeStartDate && (
              <div className="text-sm text-gray-500 md:ml-auto">
                {activeStartDate === activeEndDate
                  ? new Date(activeStartDate + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                  : `${new Date(activeStartDate + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${new Date(activeEndDate + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`
                }
              </div>
            )}
          </div>
        </div>

        {/* Range Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-2xl shadow-lg p-6 col-span-2">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 bg-blue-100 rounded-xl">
                <Eye className="w-5 h-5 text-blue-600" />
              </div>
              <span className="text-sm text-gray-500">Visits in Range</span>
            </div>
            <div className="flex items-end gap-3">
              <p className="text-4xl font-bold text-gray-800">
                {rangeStats?.totalVisits || 0}
              </p>
              {(() => {
                const change = getPercentChange(rangeStats?.totalVisits || 0, previousStats?.totalVisits || 0);
                if (!change) return null;
                return (
                  <span className={`flex items-center gap-0.5 text-sm font-semibold mb-1 ${change.value >= 0 ? 'text-green-600' : 'text-red-500'}`}>
                    {change.value >= 0 ? <TrendingUp className="w-3.5 h-3.5" /> : <TrendingDown className="w-3.5 h-3.5" />}
                    {change.label}
                  </span>
                );
              })()}
            </div>
            <p className="text-xs text-gray-400">
              {rangeStats?.dailyVisits.length || 0} day{(rangeStats?.dailyVisits.length || 0) !== 1 ? 's' : ''} of data
              {previousStats ? ` · prev: ${previousStats.totalVisits}` : ''}
            </p>
          </div>

          <div className="bg-white rounded-2xl shadow-lg p-6">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 bg-green-100 rounded-xl">
                <TrendingUp className="w-5 h-5 text-green-600" />
              </div>
              <span className="text-sm text-gray-500">Avg/Day</span>
            </div>
            {(() => {
              const currentAvg = rangeStats && rangeStats.dailyVisits.length > 0
                ? Math.round(rangeStats.totalVisits / rangeStats.dailyVisits.length) : 0;
              const prevAvg = previousStats && previousStats.dailyVisits.length > 0
                ? Math.round(previousStats.totalVisits / previousStats.dailyVisits.length) : 0;
              const change = getPercentChange(currentAvg, prevAvg);
              return (
                <>
                  <div className="flex items-end gap-2">
                    <p className="text-3xl font-bold text-gray-800">{currentAvg}</p>
                    {change && (
                      <span className={`flex items-center gap-0.5 text-xs font-semibold mb-1 ${change.value >= 0 ? 'text-green-600' : 'text-red-500'}`}>
                        {change.value >= 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                        {change.label}
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-gray-400">visits per day</p>
                </>
              );
            })()}
          </div>

          <div className="bg-white rounded-2xl shadow-lg p-6">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 bg-purple-100 rounded-xl">
                <Clock className="w-5 h-5 text-purple-600" />
              </div>
              <span className="text-sm text-gray-500">Avg Session</span>
            </div>
            {(() => {
              const change = getPercentChange(rangeStats?.avgSessionDuration || 0, previousStats?.avgSessionDuration || 0);
              return (
                <>
                  <div className="flex items-end gap-2">
                    <p className="text-3xl font-bold text-gray-800">
                      {formatDuration(rangeStats?.avgSessionDuration || 0)}
                    </p>
                    {change && (
                      <span className={`flex items-center gap-0.5 text-xs font-semibold mb-1 ${change.value >= 0 ? 'text-green-600' : 'text-red-500'}`}>
                        {change.value >= 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                        {change.label}
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-gray-400">{rangeStats?.totalSessions || 0} sessions</p>
                </>
              );
            })()}
          </div>
        </div>

        {/* All-Time Totals Bar */}
        <div className="bg-gradient-to-r from-gray-50 to-gray-100 rounded-2xl shadow-sm p-4 mb-6 border border-gray-200">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">All Time</span>
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div className="text-center">
              <p className="text-2xl font-bold text-gray-700">{rangeStats?.allTimeTotals.totalVisits || 0}</p>
              <p className="text-xs text-gray-400">Total Visits</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-gray-700">{rangeStats?.allTimeTotals.uniqueVisitors || 0}</p>
              <p className="text-xs text-gray-400">Unique Visitors</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-gray-700">{rangeStats?.allTimeTotals.returnVisitors || 0}</p>
              <p className="text-xs text-gray-400">Return Visitors</p>
            </div>
          </div>
        </div>

        {/* Language & Section Stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          {/* Languages */}
          <div className="bg-white rounded-2xl shadow-lg p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-blue-100 rounded-xl">
                <Globe className="w-5 h-5 text-blue-600" />
              </div>
              <span className="font-semibold text-gray-700">Language Usage</span>
            </div>
            <div className="space-y-3">
              {rangeStats?.languages && Object.keys(rangeStats.languages).length > 0 ? (
                Object.entries(rangeStats.languages)
                  .filter(([key]) => ['en', 'es', 'so', 'quc'].includes(key))
                  .sort(([, a], [, b]) => (b as number) - (a as number))
                  .map(([lang, count]) => {
                    const total = Object.entries(rangeStats.languages)
                      .filter(([key]) => ['en', 'es', 'so', 'quc'].includes(key))
                      .reduce((sum, [, val]) => sum + (val as number), 0);
                    const percentage = total > 0 ? Math.round(((count as number) / total) * 100) : 0;
                    return (
                      <div key={lang}>
                        <div className="flex justify-between text-sm mb-1">
                          <span className="text-gray-600">{getLanguageName(lang)}</span>
                          <span className="text-gray-800 font-medium">{count as number} ({percentage}%)</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div
                            className="bg-gradient-to-r from-blue-400 to-blue-600 h-2 rounded-full transition-all"
                            style={{ width: `${percentage}%` }}
                          ></div>
                        </div>
                      </div>
                    );
                  })
              ) : (
                <p className="text-gray-400 text-sm">No language data yet</p>
              )}
            </div>
          </div>

          {/* Sections */}
          <div className="bg-white rounded-2xl shadow-lg p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-purple-100 rounded-xl">
                <Layout className="w-5 h-5 text-purple-600" />
              </div>
              <span className="font-semibold text-gray-700">Section Views</span>
            </div>
            <div className="space-y-3">
              {rangeStats?.sections && Object.keys(rangeStats.sections).length > 0 ? (
                (() => {
                  const allSections = Object.entries(rangeStats.sections)
                    .filter(([key]) => ['home', 'appointments', 'health', 'mental-health', 'medications', 'conditions', 'warning', 'labor', 'birth-control', 'breastfeeding', 'baby-care', 'resources', 'nutrition'].includes(key))
                    .sort(([, a], [, b]) => (b as number) - (a as number));
                  const total = allSections.reduce((sum, [, val]) => sum + (val as number), 0);
                  const displayed = showAllSections ? allSections : allSections.slice(0, 4);
                  return (
                    <>
                      {displayed.map(([section, count]) => {
                        const percentage = total > 0 ? Math.round(((count as number) / total) * 100) : 0;
                        return (
                          <div key={section}>
                            <div className="flex justify-between text-sm mb-1">
                              <span className="text-gray-600">{getSectionName(section)}</span>
                              <span className="text-gray-800 font-medium">{count as number} ({percentage}%)</span>
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-2">
                              <div
                                className="bg-gradient-to-r from-purple-400 to-purple-600 h-2 rounded-full transition-all"
                                style={{ width: `${percentage}%` }}
                              ></div>
                            </div>
                          </div>
                        );
                      })}
                      {allSections.length > 4 && (
                        <button
                          onClick={() => setShowAllSections(!showAllSections)}
                          className="text-sm text-purple-600 hover:text-purple-800 font-medium mt-1"
                        >
                          {showAllSections ? 'Show Less' : `Show All (${allSections.length})`}
                        </button>
                      )}
                    </>
                  );
                })()
              ) : (
                <p className="text-gray-400 text-sm">No section data yet</p>
              )}
            </div>
          </div>
        </div>

        {/* Text-to-Speech Usage */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-pink-100 rounded-xl">
              <Volume2 className="w-5 h-5 text-pink-600" />
            </div>
            <span className="font-semibold text-gray-700">Text-to-Speech Usage</span>
            <span className="text-sm text-gray-400 ml-auto">{rangeStats?.ttsUsage || 0} total plays</span>
          </div>
          {rangeStats?.ttsBreakdown && Object.keys(rangeStats.ttsBreakdown).length > 0 ? (
            <div className="space-y-2">
              {(() => {
                const entries = Object.entries(rangeStats.ttsBreakdown)
                  .sort(([, a], [, b]) => (b as number) - (a as number));
                const total = entries.reduce((sum, [, v]) => sum + (v as number), 0);
                return entries.slice(0, 8).map(([key, count]) => {
                  const percentage = total > 0 ? Math.round(((count as number) / total) * 100) : 0;
                  return (
                    <div key={key}>
                      <div className="flex justify-between text-sm mb-1">
                        <span className="text-gray-600 truncate mr-2">{getTtsEntryName(key)}</span>
                        <span className="text-gray-800 font-medium whitespace-nowrap">{count as number} ({percentage}%)</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                          className="bg-gradient-to-r from-pink-400 to-pink-600 h-2 rounded-full transition-all"
                          style={{ width: `${percentage}%` }}
                        ></div>
                      </div>
                    </div>
                  );
                });
              })()}
              {Object.keys(rangeStats.ttsBreakdown).length > 8 && (
                <p className="text-xs text-gray-400 mt-1">
                  + {Object.keys(rangeStats.ttsBreakdown).length - 8} more
                </p>
              )}
            </div>
          ) : (
            <p className="text-gray-400 text-sm">No TTS data in this range</p>
          )}
        </div>

        {/* Daily Visits Chart */}
        {rangeStats?.dailyVisits && rangeStats.dailyVisits.length > 0 && (
          <div className="bg-white rounded-2xl shadow-lg p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-blue-100 rounded-xl">
                <Calendar className="w-5 h-5 text-blue-600" />
              </div>
              <span className="font-semibold text-gray-700">Daily Visits</span>
              <span className="text-sm text-gray-400 ml-auto">
                Total: {rangeStats.dailyVisits.reduce((sum, d) => sum + d.visits, 0)} visits
              </span>
            </div>
            <div className={`${
              rangeStats.dailyVisits.length <= 7
                ? 'grid grid-cols-7 gap-2'
                : 'flex gap-2 overflow-x-auto pb-2'
            }`}>
              {rangeStats.dailyVisits.map((day) => {
                const maxVisits = Math.max(...rangeStats.dailyVisits.map(d => d.visits), 1);
                const intensity = Math.max(0.3, day.visits / maxVisits);
                return (
                  <div key={day.date} className="text-center min-w-[60px] flex-shrink-0">
                    <div className="text-xs font-medium text-gray-500 mb-1">
                      {new Date(day.date + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'short' })}
                    </div>
                    <div className="text-xs text-gray-400 mb-1">
                      {new Date(day.date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                    </div>
                    <div
                      className="rounded-lg text-white font-bold py-3"
                      style={{ background: `rgba(59, 130, 246, ${intensity})` }}
                    >
                      {day.visits}
                    </div>
                    <div className="text-xs text-gray-400 mt-1">
                      {day.visits === 1 ? 'visit' : 'visits'}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Create New Admin */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mt-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-blue-100 rounded-xl">
              <UserPlus className="w-5 h-5 text-blue-600" />
            </div>
            <span className="font-semibold text-gray-700">Create New Admin</span>
          </div>
          <form onSubmit={handleCreateAdmin} className="space-y-3">
            <div>
              <label className="text-sm text-gray-600 mb-1 block">Email</label>
              <input
                type="email"
                value={newAdminEmail}
                onChange={(e) => setNewAdminEmail(e.target.value)}
                placeholder="newadmin@example.com"
                className="w-full px-4 py-2 rounded-xl border border-gray-300 focus:border-blue-400 focus:ring-1 focus:ring-blue-400 outline-none"
              />
            </div>
            <div>
              <label className="text-sm text-gray-600 mb-1 block">Password</label>
              <input
                type="password"
                value={newAdminPassword}
                onChange={(e) => setNewAdminPassword(e.target.value)}
                placeholder="Minimum 6 characters"
                className="w-full px-4 py-2 rounded-xl border border-gray-300 focus:border-blue-400 focus:ring-1 focus:ring-blue-400 outline-none"
              />
            </div>
            {adminCreateError && (
              <p className="text-red-600 text-sm">{adminCreateError}</p>
            )}
            {adminCreateMsg && (
              <p className="text-green-600 text-sm">{adminCreateMsg}</p>
            )}
            <button
              type="submit"
              disabled={creatingAdmin}
              className="px-4 py-2 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors font-medium disabled:opacity-50"
            >
              {creatingAdmin ? 'Creating...' : 'Create Admin'}
            </button>
          </form>
        </div>

        {/* Clear All Data */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mt-6 border-2 border-red-200">
          <div className="flex items-center gap-3 mb-3">
            <div className="p-2 bg-red-100 rounded-xl">
              <AlertTriangle className="w-5 h-5 text-red-600" />
            </div>
            <span className="font-semibold text-red-700">Danger Zone</span>
          </div>
          <p className="text-sm text-gray-600 mb-4">
            This will permanently delete all analytics data including visits, language usage, section views, text-to-speech usage, session data, and visitor records. This action cannot be undone.
          </p>
          {!showClearConfirm ? (
            <button
              onClick={() => { setShowClearConfirm(true); setClearPassword(''); setClearPasswordError(''); }}
              className="px-4 py-2 bg-red-100 text-red-700 rounded-xl hover:bg-red-200 transition-colors font-medium"
            >
              Clear All Data
            </button>
          ) : (
            <div className="bg-red-50 rounded-xl p-4 border border-red-200">
              <p className="text-red-800 font-semibold mb-3">
                Are you sure? All dashboard data will be permanently deleted.
              </p>
              <div className="mb-3">
                <label className="text-sm text-red-700 mb-1 flex items-center gap-1.5">
                  <Lock className="w-3.5 h-3.5" />
                  Enter your password to confirm
                </label>
                <input
                  type="password"
                  value={clearPassword}
                  onChange={(e) => setClearPassword(e.target.value)}
                  placeholder="Your admin password"
                  className="w-full max-w-xs px-4 py-2 rounded-xl border border-red-300 focus:border-red-500 focus:ring-1 focus:ring-red-500 outline-none text-sm"
                />
                {clearPasswordError && (
                  <p className="text-red-600 text-sm mt-1">{clearPasswordError}</p>
                )}
              </div>
              <div className="flex gap-3">
                <button
                  onClick={handleClearWithPassword}
                  disabled={clearing || !clearPassword}
                  className="px-4 py-2 bg-red-600 text-white rounded-xl hover:bg-red-700 transition-colors font-medium disabled:opacity-50"
                >
                  {clearing ? 'Clearing...' : 'Yes, Delete Everything'}
                </button>
                <button
                  onClick={() => { setShowClearConfirm(false); setClearPassword(''); setClearPasswordError(''); }}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-xl hover:bg-gray-300 transition-colors font-medium"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
